import { useLazyQuery, useQuery } from '@apollo/client/react'
import { Center, Spinner, Text } from '@chakra-ui/react'
import { ComboboxItem, ComboboxTagInput, ComboboxTagInputProps } from '@src/components/sco/ComboboxTagInput'
import { ScoTag } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { useDebounce } from '@uidotdev/usehooks'
import React, { useEffect, useState } from 'react'

export type ScoTagComboboxInputProps = {
  comboboxTagInputProps?: ComboboxTagInputProps
  defaultSelectedTagTitles?: string[]
  onFilterChange?: (filterText: string) => void
  onSelectedTagsChange?: (selectedTags: ScoTag[]) => void
  pageSize?: number
  tagTypeKeys?: string[]
}

export const ScoTagComboboxInput: React.FC<ScoTagComboboxInputProps> = props => {
  const [titleFilterValue, setTitleFilterValue] = useState<string>('')
  const debouncedTitle = useDebounce<string>(titleFilterValue, 250)
  const [selectedItemTitles, setSelectedItemTitles] = useState<string[]>(props.defaultSelectedTagTitles || [])
  const [selectedTags, setSelectedTags] = useState<ScoTag[]>([])
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false)
  const [selectedComboboxItems, setSelectedComboboxItems] = useState<ComboboxItem[]>([])

  const [runSelectionsQuery, selectionsQuery] = useLazyQuery(gql`
    query ($tagTitles: [String], $tagTypeKeys: [String], $pageSize: Int, $order: [[String]]) {
      scoTags(tagTitles: $tagTitles, tagTypeKeys: $tagTypeKeys, pageSize: $pageSize, order: $order) {
        id
        items {
          id
          slug
          tagId
          tagTypeKey
          title
        }
      }
    }
  `)

  const optionsQuery = useQuery(
    gql`
      query ($tagTypeKeys: [String], $title: String, $pageSize: Int, $order: [[String]]) {
        scoTags(tagTypeKeys: $tagTypeKeys, title: $title, pageSize: $pageSize, order: $order) {
          id
          items {
            id
            slug
            tagId
            tagTypeKey
            title
          }
        }
      }
    `,
    {
      ssr: false,
      variables: {
        tagTypeKeys: props.tagTypeKeys,
        title: debouncedTitle || undefined,
        pageSize: props.pageSize || 50,
        order: [['popularity', 'desc']],
      },
    }
  )

  useEffect(() => {
    let cancelled = false

    if (selectedItemTitles?.length > 0) {
      void runSelectionsQuery({
        variables: {
          tagTitles: selectedItemTitles,
          tagTypeKeys: props.tagTypeKeys,
          order: [['popularity', 'desc']],
          pageSize: 100,
        },
      })
        .then(result => {
          if (cancelled) {
            return
          }
          setInitialLoadComplete(true)
          setSelectedTags(result.data?.scoTags?.items || [])
          setSelectedComboboxItems(
            result.data?.scoTags?.items.map(t => ({ key: t.tagId, title: t.title })) || []
          )
        })
        .catch(() => {
          // Ignore aborted in-flight queries (e.g. rapid remount during funnel step changes).
        })
    } else {
      setSelectedTags([])
      setSelectedComboboxItems([])
      setInitialLoadComplete(true)
    }

    return () => {
      cancelled = true
    }
  }, [props.tagTypeKeys, selectedItemTitles?.join(';')])

  useEffect(() => {
    if (initialLoadComplete && !selectionsQuery.loading && props.onSelectedTagsChange) {
      props.onSelectedTagsChange(selectedTags)
    }
  }, [selectedTags?.map(tag => tag.title).join(';')])

  if (props.defaultSelectedTagTitles?.length && !initialLoadComplete) {
    if (selectionsQuery.error || optionsQuery.error) {
      return (
        <Center bgColor={'blackAlpha.300'} borderRadius={'sm'} paddingY={4}>
          <Text>
            {'Error loading tags: '}
            {selectionsQuery.error?.toString() || selectionsQuery.error?.toString()}
          </Text>
        </Center>
      )
    }
    return (
      <Center bgColor={'blackAlpha.300'} borderRadius={'sm'} paddingY={4}>
        <Spinner />
      </Center>
    )
  }

  const comboboxItems: ComboboxItem[] =
    optionsQuery.data?.scoTags?.items.map(tag => ({
      key: tag.tagId,
      title: tag.title,
      value: tag,
    })) || []

  return (
    <>
      <ComboboxTagInput
        allowMultipleSelections={true}
        defaultSelectedItems={selectedComboboxItems}
        delayLoadingState={true}
        hideItemsAfterSelect={true}
        items={comboboxItems}
        itemsAreFiltered={true}
        itemsAreLoading={optionsQuery.loading}
        onClear={() => setSelectedItemTitles([])}
        onChange={values => {
          setSelectedItemTitles(values.map(value => value.title))
        }}
        onInputChange={value => {
          setTitleFilterValue(value)
        }}
        {...props.comboboxTagInputProps}
      />
    </>
  )
}
